import { useNavigate } from "react-router-dom";
import { KeyRound, Lock } from "lucide-react";
import { useAuthStore } from "@/stores/auth-store";
import { BrandMark } from "@/components/shell/brand-logo";

export default function SessionExpiredPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const goMfa = useAuthStore((s) => s.goMfa);
  const differentUser = useAuthStore((s) => s.differentUser);

  // "Sign in again" re-authenticates the *same* user, so it goes straight to
  // MFA instead of the full login screen. Kept local to this page (rather
  // than changing `goLogin`) since `goLogin` is also used by "different
  // user?" links elsewhere, where routing to /login is still correct.
  function reauth(method: "passkey" | "totp") {
    goMfa(method);
    navigate("/mfa");
  }

  function signOut() {
    differentUser();
    navigate("/login");
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-bg">
      {/* Dimmed placeholder for the live app behind the modal — deliberately
          not the real dashboard (out of Auth's scope), just enough shape to
          read as "your session, paused" per the canonical LOG-04 frame. */}
      <div aria-hidden className="pointer-events-none select-none px-10 py-8">
        <div className="mx-auto flex max-w-5xl flex-col gap-4">
          <div className="h-10 w-full rounded-lg bg-line" />
          <div className="h-10 w-full rounded-lg bg-line" />
          <div className="h-10 w-full rounded-lg bg-line" />
        </div>
      </div>
      <div aria-hidden className="absolute inset-0 bg-ink/45" />

      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="auth-card">
          <div className="auth-card__inner">
            <div className="auth-card__content" style={{ maxWidth: 380 }}>
              <div className="grid size-14 place-items-center rounded-xl bg-primary text-white">
                <BrandMark size={32} />
              </div>

              <div className="flex w-full flex-col gap-6">
                <div className="auth-heading">
                  <h1 className="auth-title" style={{ fontSize: 24 }}>Session Expired</h1>
                  {user?.email && <p className="auth-subtitle" style={{ fontSize: 15 }}>{user.email}</p>}
                </div>

                <div className="flex w-full items-start gap-2 rounded-lg bg-primary-soft px-3.5 py-3 text-left">
                  <Lock className="mt-0.5 size-3.5 shrink-0 text-primary" aria-hidden />
                  <p className="text-xs leading-relaxed text-primary">
                    Your session has expired due to inactivity. Re-authenticate to continue where you left off.
                  </p>
                </div>

                <div className="w-full overflow-hidden rounded-lg border border-line">
                  {[
                    ["Last active", "10:15 UTC"],
                    ["Expired at", "10:45 UTC"],
                    ["Context saved", "Yes — restored"],
                  ].map(([k, v], i) => (
                    <div key={k} className={`flex items-center justify-between gap-3 px-3.5 py-2.5 text-xs ${i > 0 ? "border-t border-line" : ""}`}>
                      <span className="text-muted">{k}</span>
                      <span className={k === "Context saved" ? "font-semibold text-success" : "font-semibold text-ink"}>{v}</span>
                    </div>
                  ))}
                </div>

                <div className="flex w-full flex-col items-center gap-3">
                  <button
                    type="button"
                    onClick={() => reauth("passkey")}
                    aria-label="Use your passkey to continue"
                    className="flex h-14 w-14 items-center justify-center rounded-[16px] bg-gradient-to-br from-success to-success-vivid outline-none transition-transform hover:scale-105 focus-visible:ring-2 focus-visible:ring-primary-soft"
                  >
                    <KeyRound className="size-7 text-white" aria-hidden />
                  </button>
                  <p className="text-sm text-muted">Use your passkey to continue</p>
                </div>

                <div className="flex w-full items-center justify-center gap-2 text-xs font-medium">
                  <button type="button" onClick={signOut} className="text-ink outline-none hover:underline">
                    Sign out instead
                  </button>
                  <span className="text-muted">·</span>
                  <button type="button" onClick={() => reauth("totp")} className="text-primary outline-none hover:underline">
                    Use TOTP
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
